import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isAuthenticated } from '../lib/auth';

const PLANS = [
  { key: 'free', price: '0', popular: false },
  { key: 'starter', price: '9', popular: true },
  { key: 'pro', price: '19', popular: false },
];

const Check = () => (
  <svg className="w-5 h-5 flex-shrink-0 text-emerald-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0L3.3 9.7a1 1 0 111.4-1.4l3.3 3.3 6.8-6.8a1 1 0 011.4 0z" clipRule="evenodd" />
  </svg>
);

const PricingSection = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const subscribe = (planKey) => {
    localStorage.setItem('selectedPlan', planKey);
    if (isAuthenticated()) {
      navigate('/creer-radio/step1');
    } else {
      localStorage.setItem('fromCreateRadio', 'true');
      navigate('/signup');
    }
  };

  return (
    <section id="pricing" className="py-20 px-4 scroll-mt-24">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-3">{t('pricing.title')}</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">{t('pricing.subtitle')}</p>
        </div>

        <div className="grid gap-8 md:grid-cols-3 items-stretch">
          {PLANS.map((plan) => {
            const features = ['f1', 'f2', 'f3', 'f4', 'f5'];
            return (
              <div
                key={plan.key}
                className={`relative flex flex-col rounded-3xl p-8 transition ${
                  plan.popular
                    ? 'bg-gradient-to-br from-[#4c1d95] to-[#6d28d9] text-white shadow-2xl md:scale-105 ring-1 ring-purple-300/40'
                    : 'bg-white text-gray-900 border border-gray-100 shadow-sm hover:shadow-lg'
                }`}
              >
                {plan.popular && (
                  <span className="absolute -top-3 start-1/2 -translate-x-1/2 rtl:translate-x-1/2 bg-amber-400 text-purple-900 text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-full shadow">
                    {t('pricing.popular')}
                  </span>
                )}

                <h3 className={`text-xl font-bold mb-1 ${plan.popular ? 'text-white' : 'text-purple-600'}`}>
                  {t(`pricing.${plan.key}.name`)}
                </h3>
                <p className={`text-sm mb-5 ${plan.popular ? 'text-purple-100/80' : 'text-gray-500'}`}>
                  {t(`pricing.${plan.key}.tagline`)}
                </p>

                <div className="mb-6 flex items-end gap-1">
                  <span className="text-4xl font-extrabold">€{plan.price}</span>
                  <span className={`text-base font-medium mb-1 ${plan.popular ? 'text-purple-100/80' : 'text-gray-500'}`}>
                    {t('pricing.perMonth')}
                  </span>
                </div>

                <ul className="space-y-3 mb-8 flex-1">
                  {features.map((f) => (
                    <li key={f} className="flex items-center gap-2.5 text-sm">
                      <Check />
                      <span className={plan.popular ? 'text-purple-50' : 'text-gray-700'}>
                        {t(`pricing.${plan.key}.${f}`)}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => subscribe(plan.key)}
                  className={`w-full py-3 rounded-full font-semibold transition ${
                    plan.popular
                      ? 'bg-white text-purple-700 hover:bg-purple-50'
                      : 'bg-purple-600 text-white hover:bg-purple-700'
                  }`}
                >
                  {t('pricing.cta')}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
